import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import PillInput from './PillInput';
import { COLORS, TYPOGRAPHY } from '../../../styles/UISystem';

/**
 * NicknameField — the owner's short "how I know them" descriptor for a
 * connection. Viewer-private, DB-first (connection_views.nickname): the caller
 * passes the context value and an onSave that runs updateNickname.
 *
 * White + soft shadow with a light placeholder — the Note field's inviting
 * treatment (user call at device pass, 2026-07-19).
 *
 * NO clear button (user ruling, 2026-08-19). Contacts keep theirs because there
 * the X is structural — clearing and saving is how you delete a field you no
 * longer want shared. A note has no field to remove, so an X there only ever
 * means "destroy everything I wrote", and it would sit exactly where a thumb
 * taps to put the caret at the end of the text. The loss is unrecoverable: the
 * note is DB-first, blur writes the empty string over it, and iOS shake-to-undo
 * doesn't cover a programmatic clear. Select-all-and-delete is the right cost
 * for a rare destructive action.
 *
 * The stored value syncs in until the person edits (the cache paint → DB
 * refresh can lag mount; never clobber typed text). Saves on blur, only when
 * the trimmed text actually changed — a failed DB write leaves state unchanged
 * upstream, so the field simply keeps what was typed.
 *
 * Placeholder is simply "Note" (frames + user ruling, 2026-08-18). It used to
 * compose "About {firstName}", which existed to stop people reading the field as
 * a way to send the person a message — a real confusion in an earlier flow. That
 * flow is gone, this field sits under a name on a record page, and the shorter
 * word is clearer, so the composition is retired rather than kept as an option
 * nothing passes.
 *
 * @param {string} value - the stored note (context, cache-painted)
 * @param {function} onSave - (trimmedText) blur-save via updateNickname
 * @param {string} placeholder - override, for a surface that needs its own word
 */
const NicknameField = ({ value = '', onSave, placeholder = 'Note' }) => {
  const [text, setText] = useState(value || '');
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (!touched) setText(value || '');
  }, [value, touched]);

  const handleBlur = () => {
    const next = text.trim();
    if (next === (value || '').trim()) return;
    onSave?.(next);
  };

  // Empty field reads light and quiet (the Note field's placeholder state);
  // typed text reads regular. Left-justified (exchange-polish item 9,
  // supersedes the centered treatment) — it reads as a field to fill, not a
  // card headline.
  const stateStyle = {
    textAlign: 'left',
    ...(text
      ? { ...TYPOGRAPHY.BODY_REGULAR, color: COLORS.DARK_GRAY }
      : { ...TYPOGRAPHY.BODY_LIGHT, fontWeight: '300' }),
  };

  return (
    <PillInput
      value={text}
      onChange={(e) => {
        setTouched(true);
        setText(e.target.value);
      }}
      onBlur={handleBlur}
      placeholder={placeholder}
      variant="shadow"
      // Grows and wraps: a note is prose, and a one-line field hides everything
      // past the first line — including on return, where it stayed unreadable.
      multiline
      autoCapitalize="sentences"
      inputStyle={stateStyle}
    />
  );
};

NicknameField.propTypes = {
  value: PropTypes.string,
  onSave: PropTypes.func,
  placeholder: PropTypes.string,
};

export default NicknameField;
